import React, { useState, useEffect, useCallback } from 'react'
import { ArrowIcon } from './components/Icons'

// Separate component to handle image loading state
function MediaImage({ item }) {
  const [imageLoaded, setImageLoaded] = useState(false)

  console.log({imageLoaded})
  
  useEffect(() => {
    // Check if image is already cached
    const img = new Image()
    img.src = `assets/expeditions-data/images/${encodeURIComponent(item.filename)}`
    
    if (img.complete) {
      // Image is already in browser cache
      setTimeout(() => {
        setImageLoaded(true)
      }, 3000)
    }
  }, [item])
  
  return (
    <div className="media-modal__image-container">
      {/* Full quality image */}
      <img 
        src={`assets/expeditions-data/images/${encodeURIComponent(item.filename)}`}
        alt={item.filename}
        className={`media-modal__full-image ${imageLoaded ? '-loaded' : ''}`}
        onLoad={() => {
          setImageLoaded(true)
        }}
      />

      
      {/* Always show LQIP initially */}
      {item.lqip && (
        <img 
        src={item.lqip}
        alt=""
        className={`media-modal__lqip ${imageLoaded ? '-hidden' : ''}`}
        aria-hidden="true"
        />
      )}
      
      {/* Always show spinner when not loaded */}
      {!imageLoaded && (
        <div className="media-modal__loading-spinner">
          <div className="spinner"></div>
        </div>
      )}
    </div>
  )
}

export function MediaModal({ item, allItems, onClose, onNavigate }) {
  // Calculate current index and navigation availability
  const currentIndex = item && allItems.length > 0
    ? allItems.findIndex(img => img.filename === item.filename)
    : -1
  // Enable cycling through images
  const hasNext = allItems.length > 1
  const hasPrev = allItems.length > 1

  const isVideo = item ? (item.vimeo_id && item.vimeo_id !== '') : false

  const handleNavigateClick = useCallback((direction) => {
    // Use modulo arithmetic to wrap around
    const newIndex = (currentIndex + direction + allItems.length) % allItems.length
    onNavigate(allItems[newIndex])
  }, [currentIndex, allItems, onNavigate])

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!item) return

      // ESC closes modal
      if (e.key === 'Escape') {
        onClose()
        return
      }

      // Arrow navigation with cycling
      if (e.key === 'ArrowLeft' && hasPrev) {
        e.preventDefault()
        handleNavigateClick(-1)
      } else if (e.key === 'ArrowRight' && hasNext) {
        e.preventDefault()
        handleNavigateClick(1)
      }
    }

    if (item) {
      window.addEventListener('keydown', handleKeyDown)
      return () => window.removeEventListener('keydown', handleKeyDown)
    }
  }, [item, hasNext, hasPrev, onClose, handleNavigateClick])
  
  if (!item) return null
  
  return (
    <div className="media-modal__backdrop" onClick={onClose}>
      {/* Navigation arrows outside content */}
      {hasPrev && (
        <button 
          className="media-modal__nav media-modal__nav--prev" 
          onClick={(e) => {
            e.stopPropagation()
            handleNavigateClick(-1)
          }}
          aria-label="Previous image"
        >
          <ArrowIcon className="-flip" />
        </button>
      )}
      
      <div className="media-modal__content" onClick={(e) => e.stopPropagation()}>
        <div className="media-modal__controls">
          <a 
            href={`assets/expeditions-data/images/${encodeURIComponent(item.filename)}`}
            download={item.filename}
            className="media-modal__download"
            aria-label="Download image"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M11.0832 5.25H8.74984V1.75H5.24984V5.25H2.9165L6.99984 9.33333L11.0832 5.25ZM2.9165 10.5V11.6667H11.0832V10.5H2.9165Z" />
            </svg>
          </a>
          <button className="media-modal__close" onClick={onClose}>
            <img src="assets/icons/close.svg" alt="Close" />
          </button>
        </div>
        
        <div className="media-modal__media">
          {isVideo ? (
            <iframe
              src={`https://player.vimeo.com/video/${item.vimeo_id}?autoplay=1`}
              width="100%"
              height="100%"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              title={`Video: ${item.filename}`}
            />
          ) : (
            <MediaImage key={item.filename} item={item} />
          )}
        </div>
        
        <div className="media-modal__metadata">
          <div className="metadata-grid">
            {item.organisms && item.organisms.length > 0 && (
              <div>
                <p className="-bold">Organisms:</p>
                <p>{item.organisms.join(', ')}</p>
              </div>
            )}
            {item.cephalopod_behavior && item.cephalopod_behavior.length > 0 && (
              <div>
                <p className="-bold">Behavior:</p>
                <p>{item.cephalopod_behavior.join(', ')}</p>
              </div>
            )}
            {item.site_name && (
              <div>
                <p className="-bold">Site:</p>
                <p>{item.site_name} ({item.site_type})</p>
              </div>
            )}
            {item.date && (
              <div>
                <p className="-bold">Date:</p>
                <p>{item.date}</p>
              </div>
            )}
            {item.depth && (
              <div>
                <p className="-bold">Depth:</p>
                <p>{item.depth} m ({item.depth_range})</p>
              </div>
            )}
            {item.time_range && (
              <div>
                <p className="-bold">Time:</p>
                <p>{item.time_range}</p>
              </div>
            )}
            {item.lens_type && (
              <div>
                <p className="-bold">Lens:</p>
                <p>{item.lens_type}</p>
              </div>
            )}
            <div>
              <p className="-bold">Flash:</p>
              <p>{item.flash ? 'Yes' : 'No'}</p>
            </div>
          </div>
        </div>
      </div>
      
      {hasNext && (
        <button 
          className="media-modal__nav media-modal__nav--next" 
          onClick={(e) => {
            e.stopPropagation()
            handleNavigateClick(1)
          }}
          aria-label="Next image"
        >
          <ArrowIcon />
        </button>
      )}
    </div>
  )
}

